import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Helmet } from 'react-helmet';
import { useApolloClient } from '@apollo/client';
import { Alert, IconButton, Button, Grid, CircularProgress, LinearProgress, Box, Typography, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';

import { useQuery, useMutation } from '@apollo/client';
import { gql as apolloGql } from '@apollo/client';
import i18next from 'i18next';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CancelIcon from '@mui/icons-material/Cancel';

import { useForm, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import yup from 'validation';

import { dispatchException, dispatchMessage } from 'helper/snackbar';

import { FormInputText } from 'components/form/FormInputText';
import { FormInputNumber } from 'components/form/FormInputNumber';
import { FormInputPercentage } from 'components/form/FormInputPercentage';
import { FormInputDate } from 'components/form/FormInputDate';
import { FormInputCheckbox } from 'components/form/FormInputCheckbox';
import { FormInputCountryDropdown } from 'components/form/FormInputDropdown';
import { UnsavedChangesPrompt } from 'components/form/UnsavedChangesPrompt';
import CustomTabs from 'components/Tabs';
import SimpleTable from 'components/table/SimpleTable';
import ConfirmationButton from 'components/dialogs/ConfirmationButton';
import { SpaceSelectionInput } from 'components/security/SpaceSelectionInput';

import {
  TAXTYPE_VIEW_QUERY,
  UPDATE_TAXTYPE_MUTATION,
  CREATE_TAXTYPE_MUTATION,
  DELETE_TAXTYPE_MUTATION,
  REFETCH_TAXTYPES_QUERIES,
  EVICT_TAXTYPES_QUERIES,
  TAXTYPE_REFRESH_PROGRESS_QUERY,
  CANCEL_TAXTYPE_REFRESH_MUTATION,
  GLOBAL_TAXTYPE_REFRESH_PROGRESS_QUERY,
  CANCEL_GLOBAL_TAXTYPE_REFRESH_MUTATION,
} from '../prices/gql';
import { ECountryCode, TaxTypeListOutput } from '__generated__/graphql';

import { userSelector, canEditAdminRecord, initialSpaceId } from 'helper/security';
import { filterSelector } from 'helper/filter';
import { RedirectError } from 'pages/error';
import { formatDocumentTitle } from 'helper/usedocumenttitle';

interface TaxTypeProps {
  id: number;
}
interface TaxTypeCreateProps { }
interface TaxTypeFormProps {
  data: TaxTypeListOutput;
}

type GlobalTaxTypeRefreshProgress = {
  isRunning: boolean;
  progress: number;
  spacesTotal: number;
  spacesDone: number;
  hotelsTotal: number;
  hotelsDone: number;
  currentSpaceId?: number | null;
  error?: string | null;
};

const rateSchema = yup.object().shape({
  id: yup.number(),
  rate: yup.number().min(0).max(100).required().label(i18next.t('taxtype-rate-rate')).transform((value) => Number.isNaN(value) ? null : value),
  name: yup.string().required().label(i18next.t('taxtype-rate-name')),
  country: yup.mixed<ECountryCode>().oneOf(Object.values(ECountryCode)).nullable(),
  isReverseCharge: yup.boolean().required(),
  validFrom: yup.date().nullable(),
  validTo: yup.date().nullable(),
});

const validationSchema = yup
  .object()
  .shape({
    spaceId: yup.number().nullable(),
    name: yup.string().required().label(i18next.t('taxtype-name')),
    sequence: yup.number().required().label(i18next.t('taxtype-sequence')),
    rates: yup.array().required().min(1).of(rateSchema),
  })
  .test('taxtype-rates', `${i18next.t('taxtype-rates')}`, value => value.rates.length > 0);

// Constants
// Note: Frontend uses percentage values (0-100), backend uses decimal (0-1)
// So frontend epsilon needs to be 100x larger: 1e-6 * 100 = 0.0001
const RATE_COMPARISON_EPSILON = 0.0001; // For percentage values (0-100), equivalent to 1e-6 for decimal
const REFRESH_STARTING_WINDOW_MS = 30000; // Poll for up to 30s after save to catch scheduler start delay

// Untyped query for space name lookup (similar to global refresh query)
const SPACE_NAME_QUERY = apolloGql(`
  query ViewSpaceName($id: Int!) {
    viewSpace(id: $id) {
      id
      name
    }
  }
`);

function TaxTypeForm(props: TaxTypeFormProps) {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const debugRefresh = (() => {
    if (typeof window === 'undefined') return false;
    const fromSearch = new URLSearchParams(window.location.search).get('debugRefresh') === '1';
    // In some setups the query params may end up after a '#' (hash) – support that too.
    const hash = window.location.hash || '';
    const hashQuery = hash.includes('?') ? hash.slice(hash.indexOf('?')) : '';
    const fromHash = hashQuery ? new URLSearchParams(hashQuery).get('debugRefresh') === '1' : false;
    return fromSearch || fromHash;
  })();

  const [updateMutateFunction] = useMutation(UPDATE_TAXTYPE_MUTATION);
  const [createMutateFunction] = useMutation(CREATE_TAXTYPE_MUTATION);
  const [deleteMutateFunction] = useMutation(DELETE_TAXTYPE_MUTATION);
  const [cancelTaxTypeRefreshMutation] = useMutation(CANCEL_TAXTYPE_REFRESH_MUTATION);
  const [cancelGlobalTaxTypeRefreshMutation] = useMutation(CANCEL_GLOBAL_TAXTYPE_REFRESH_MUTATION);

  // Progress tracking for tax type refresh (space-specific)
  const spaceId = props.data.space?.id ?? undefined;
  const { data: refreshProgressData, startPolling: startRefreshPolling, stopPolling: stopRefreshPolling } = useQuery(
    TAXTYPE_REFRESH_PROGRESS_QUERY,
    {
      variables: { spaceId: spaceId ?? 0 },
      skip: !spaceId,
      fetchPolicy: 'network-only',
    },
  );

  // Progress tracking for global tax type refresh
  const taxTypeId = props.data.id;
  const isGlobal = !spaceId;
  const {
    data: globalRefreshProgressData,
    startPolling: startGlobalRefreshPolling,
    stopPolling: stopGlobalRefreshPolling,
  } = useQuery(GLOBAL_TAXTYPE_REFRESH_PROGRESS_QUERY, {
    variables: { taxTypeId },
    skip: !isGlobal || taxTypeId < 0,
    fetchPolicy: 'network-only',
  });

  // Extract progress data (space-specific or global) - use type-safe helper
  const progress = isGlobal
    ? globalRefreshProgressData?.globalTaxTypeRefreshProgress
    : refreshProgressData?.taxTypeRefreshProgress;

  const isRefreshing = progress?.isRunning ?? false;
  const refreshProgress = progress?.progress ?? 0;
  const hotelsTotal = progress?.hotelsTotal ?? 0;
  const hotelsDone = progress?.hotelsDone ?? 0;
  const refreshError = progress?.error;
  // Global-specific fields (type-safe check with type guard)
  const globalProgress =
    isGlobal && progress && 'spacesTotal' in progress
      ? (progress as GlobalTaxTypeRefreshProgress)
      : null;
  const spacesTotal = globalProgress?.spacesTotal ?? 0;
  const spacesDone = globalProgress?.spacesDone ?? 0;
  const currentSpaceId = globalProgress?.currentSpaceId;

  // Track when refresh was just triggered (for "starting..." UI)
  const [isRefreshStarting, setIsRefreshStarting] = useState(false);
  const [startingWindowTimeoutId, setStartingWindowTimeoutId] = useState<ReturnType<typeof setTimeout> | null>(null);

  // Confirmation dialog state for refresh
  const [showRefreshConfirmDialog, setShowRefreshConfirmDialog] = useState(false);
  const [pendingSubmitValues, setPendingSubmitValues] = useState<TaxTypeFormType | null>(null);

  // Show refresh UI if refresh is running OR if we're in the starting window
  const shouldShowRefreshBox = isRefreshing || isRefreshStarting || !!progress;

  // Simple polling pattern like SystemTab: poll when refreshing OR starting, stop when neither
  useEffect(() => {
    if (isRefreshing) {
      // Clear starting state once refresh is confirmed running
      setIsRefreshStarting(false);
      if (startingWindowTimeoutId) {
        clearTimeout(startingWindowTimeoutId);
        setStartingWindowTimeoutId(null);
      }
      // Start polling
      if (isGlobal) {
        startGlobalRefreshPolling(2000);
      } else if (spaceId) {
        startRefreshPolling(2000);
      }
    } else if (isRefreshStarting) {
      // Keep polling during starting window (scheduler may not have started yet)
      if (isGlobal) {
        startGlobalRefreshPolling(2000);
      } else if (spaceId) {
        startRefreshPolling(2000);
      }
    } else {
      // Stop polling when neither refreshing nor starting
      stopGlobalRefreshPolling();
      stopRefreshPolling();
    }
  }, [isRefreshing, isRefreshStarting, isGlobal, spaceId, startRefreshPolling, stopRefreshPolling, startGlobalRefreshPolling, stopGlobalRefreshPolling, startingWindowTimeoutId]);

  // Cleanup starting window timeout on unmount
  useEffect(() => {
    return () => {
      if (startingWindowTimeoutId) {
        clearTimeout(startingWindowTimeoutId);
      }
    };
  }, [startingWindowTimeoutId]);

  const startRefreshPollingAfterSave = () => {
    if (debugRefresh) {
      // eslint-disable-next-line no-console
      console.log('[TaxTypeRefresh] startRefreshPollingAfterSave', { isGlobal, spaceId, taxTypeId });
    }

    // Clear any existing starting window timeout
    if (startingWindowTimeoutId) {
      clearTimeout(startingWindowTimeoutId);
    }

    // Show "starting..." UI immediately
    setIsRefreshStarting(true);

    // Start polling immediately (like SystemTab does)
    if (isGlobal) {
      startGlobalRefreshPolling(2000);
    } else if (spaceId) {
      startRefreshPolling(2000);
    }

    // Clear starting state after window expires (if refresh hasn't started by then)
    const timeoutId = setTimeout(() => {
      setIsRefreshStarting(false);
      setStartingWindowTimeoutId(null);
    }, REFRESH_STARTING_WINDOW_MS);
    setStartingWindowTimeoutId(timeoutId);
  };

  useEffect(() => {
    if (!debugRefresh) return;
    // eslint-disable-next-line no-console
    console.log('[TaxTypeRefresh] state', {
      isGlobal,
      spaceId,
      taxTypeId,
      isRefreshStarting,
      isRefreshing,
      hasProgressPayload: !!progress,
      shouldShowRefreshBox,
      refreshProgress,
      spacesDone,
      spacesTotal,
      hotelsDone,
      hotelsTotal,
      currentSpaceId,
      refreshError,
    });
  }, [
    debugRefresh,
    isGlobal,
    spaceId,
    taxTypeId,
    isRefreshStarting,
    isRefreshing,
    progress,
    shouldShowRefreshBox,
    refreshProgress,
    spacesDone,
    spacesTotal,
    hotelsDone,
    hotelsTotal,
    currentSpaceId,
    refreshError,
  ]);

  const [createdId, setCreatedId] = useState(0);
  useEffect(() => {
    if (createdId > 0) navigate(`/administration/taxtypes/${createdId}`);
  }, [createdId]);

  // Space name lookup for global refresh progress display
  const { data: spaceNameData } = useQuery(SPACE_NAME_QUERY, {
    variables: { id: currentSpaceId ?? 0 },
    skip: !isGlobal || currentSpaceId == null || currentSpaceId <= 0,
    fetchPolicy: 'cache-first',
  });

  const currentSpaceName = spaceNameData?.viewSpace?.name;

  const user = userSelector()!;
  const canEdit = props.data.id < 0 || canEditAdminRecord(user, props.data);

  type TaxTypeFormType = yup.InferType<typeof validationSchema>;

  const toFormSchema = (obj: TaxTypeListOutput): TaxTypeFormType => ({
    spaceId: obj.space?.id || null,
    name: obj.name,
    sequence: obj.sequence,
    rates: obj.rates.map(rate => ({
      ...rate,
      country: rate.country,
      rate: Math.round(rate.rate * 10000) / 100,
      validFrom: rate.validFrom ? new Date(rate.validFrom) : null,
      validTo: rate.validTo ? new Date(rate.validTo) : null,
    })),
  });

  const {
    handleSubmit,
    control,
    trigger,
    reset,
    formState: { errors: validationErrors, isDirty, isValidating, isSubmitting },
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(validationSchema) as any,
    context: { client: useApolloClient() },
    defaultValues: toFormSchema((props.data || {}) as TaxTypeListOutput),
  });

  const {
    fields: ratesFields,
    append: ratesAppend,
    remove: ratesRemove,
  } = useFieldArray({
    control,
    name: 'rates',
  });
  useEffect(() => {
    trigger();
  }, [trigger]);

  const onSubmit = async (values: TaxTypeFormType) => {
    try {
      // Compute shouldRefresh for updates (only for existing tax types)
      let shouldRefresh = false;
      if (props.data.id > 0) {
        const originalData = toFormSchema(props.data);

        // Validate original data exists
        if (!originalData || !Array.isArray(originalData.rates)) {
          // Don't dispatch message here - let dispatchException handle it to avoid double messages
          throw new Error('Invalid original tax type data');
        }

        // Check metadata changes (name, sequence)
        const metaChanged =
          values.name !== originalData.name ||
          values.sequence !== originalData.sequence;

        // Check rate changes
        // Define explicit type for rate normalization to improve type safety
        type RateForComparison = {
          rate: number;
          name: string;
          country: ECountryCode | null;
          isReverseCharge: boolean;
          validFrom: number | null;
          validTo: number | null;
        };

        const normalizeRate = (rate: TaxTypeFormType['rates'][0]): RateForComparison => ({
          rate: rate.rate,
          name: rate.name,
          country: rate.country ?? null,
          isReverseCharge: rate.isReverseCharge,
          validFrom: rate.validFrom?.getTime() ?? null,
          validTo: rate.validTo?.getTime() ?? null,
        });

        // Edge case: empty array means all rates are deleted
        if (!Array.isArray(values.rates) || values.rates.length === 0) {
          const ratesChanged = originalData.rates.length > 0;
          shouldRefresh = metaChanged || ratesChanged;
        } else {
          // Create Map for O(1) lookup instead of O(n) find() in loop
          const originalRatesMap = new Map(originalData.rates.filter(r => r.id != null).map(r => [r.id!, r]));

          // Check for created rates (incoming without id) - use != null to handle 0 IDs correctly
          const created = values.rates.some(r => r.id == null);

          // Check for deleted rates (original id not in incoming) - optimized check, use != null
          const originalIds = new Set(originalData.rates.filter(r => r.id != null).map(r => r.id!));
          const incomingIds = new Set(values.rates.filter(r => r.id != null).map(r => r.id!));
          const deleted = originalIds.size !== incomingIds.size ||
            Array.from(originalIds).some(rateId => !incomingIds.has(rateId));

          // Check for updated rates (compare normalized rates with epsilon for floats)
          const updated = values.rates.some(incomingRate => {
            if (incomingRate.id == null) return false; // new rate, handled by created check
            const originalRate = originalRatesMap.get(incomingRate.id);
            if (!originalRate) {
              // Rate ID exists in incoming but not in original - this shouldn't happen
              // but we'll treat it as a change to be safe
              return true;
            }
            const normalizedOriginal = normalizeRate(originalRate);
            const normalizedIncoming = normalizeRate(incomingRate);
            return (
              Math.abs(normalizedOriginal.rate - normalizedIncoming.rate) > RATE_COMPARISON_EPSILON ||
              normalizedOriginal.name !== normalizedIncoming.name ||
              normalizedOriginal.country !== normalizedIncoming.country ||
              normalizedOriginal.isReverseCharge !== normalizedIncoming.isReverseCharge ||
              normalizedOriginal.validFrom !== normalizedIncoming.validFrom ||
              normalizedOriginal.validTo !== normalizedIncoming.validTo
            );
          });

          const ratesChanged = created || deleted || updated;
          shouldRefresh = metaChanged || ratesChanged;
        }
      }

      // If refresh is needed and this is an update, show confirmation dialog
      if (props.data.id > 0 && shouldRefresh) {
        setPendingSubmitValues(values);
        setShowRefreshConfirmDialog(true);
        return; // Wait for user confirmation
      }

      // Continue with save (no refresh or new record)
      await performSave(values, shouldRefresh);
    } catch (err) {
      dispatchException(dispatch, err);
    }
  };

  const performSave = async (values: TaxTypeFormType, shouldRefresh: boolean) => {
    try {
      if (props.data.id > 0) {
        const res = await updateMutateFunction({
          variables: {
            id: props.data.id,
            data: {
              name: values.name,
              sequence: values.sequence,
              rates: values.rates.map(c => ({
                id: c.id,
                rate: c.rate / 100,
                name: c.name,
                isReverseCharge: c.isReverseCharge,
                country: c.country,
                validFrom: c.validFrom,
                validTo: c.validTo,
              })),
            },
          },
          update: cache => EVICT_TAXTYPES_QUERIES(cache),
          awaitRefetchQueries: true,
          refetchQueries: REFETCH_TAXTYPES_QUERIES(props.data.id),
        });
        reset(toFormSchema((res.data!.updateTaxType || {}) as TaxTypeListOutput));

        // Always start refresh detection/polling after an update.
        // Backend may still refresh even if our shouldRefresh heuristic is false.
        startRefreshPollingAfterSave();

        // Only show background refresh snackbar and start polling if refresh is triggered
        if (shouldRefresh) {
          dispatchMessage(
            dispatch,
            i18next.t('taxtype-saved-bg-refresh'),
          );
        } else {
          dispatchMessage(
            dispatch,
            i18next.t('taxtype-saved'),
          );
        }
      } else {
        const res = await createMutateFunction({
          variables: {
            spaceId: values.spaceId !== null ? values.spaceId : null,
            data: {
              name: values.name,
              sequence: values.sequence,
              rates: values.rates.map(c => ({
                rate: c.rate / 100,
                name: c.name,
                isReverseCharge: c.isReverseCharge,
                country: c.country,
                validFrom: c.validFrom,
                validTo: c.validTo,
              })),
            },
          },
          update: cache => EVICT_TAXTYPES_QUERIES(cache),
          awaitRefetchQueries: true,
          refetchQueries: REFETCH_TAXTYPES_QUERIES(),
        });
        reset(toFormSchema((res.data!.createTaxType || {}) as TaxTypeListOutput));
        setCreatedId(res.data!.createTaxType.id);
        dispatchMessage(
          dispatch,
          i18next.t('taxtype-saved'),
        );
      }
    } catch (err) {
      dispatchException(dispatch, err);
    }
  };

  const handleConfirmRefresh = async () => {
    setShowRefreshConfirmDialog(false);
    if (pendingSubmitValues) {
      await performSave(pendingSubmitValues, true);
      setPendingSubmitValues(null);
    }
  };

  const handleCancelRefreshDialog = () => {
    setShowRefreshConfirmDialog(false);
    setPendingSubmitValues(null);
  };

  const handleCancelRefresh = async () => {
    try {
      // Clear starting state if canceling during starting phase
      if (isRefreshStarting) {
        setIsRefreshStarting(false);
        if (startingWindowTimeoutId) {
          clearTimeout(startingWindowTimeoutId);
          setStartingWindowTimeoutId(null);
        }
        stopGlobalRefreshPolling();
        stopRefreshPolling();
      }
      if (isGlobal) {
        await cancelGlobalTaxTypeRefreshMutation({ variables: { taxTypeId } });
      } else if (spaceId) {
        await cancelTaxTypeRefreshMutation({ variables: { spaceId } });
      } else {
        return;
      }
      dispatchMessage(dispatch, i18next.t('taxtype-refresh-cancelled'));
    } catch (err) {
      dispatchException(dispatch, err);
    }
  };

  return (
    <>
      <Helmet>
        <title>{formatDocumentTitle([i18next.t('taxtypes-list-page-title'), props.data])}</title>
      </Helmet>

      {/* Confirmation Dialog for Refresh */}
      <Dialog
        open={showRefreshConfirmDialog}
        onClose={handleCancelRefreshDialog}
        aria-labelledby="refresh-confirm-dialog-title"
        aria-describedby="refresh-confirm-dialog-description"
      >
        <DialogTitle id="refresh-confirm-dialog-title">
          {i18next.t('taxtype-refresh-confirm-title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="refresh-confirm-dialog-description">
            {isGlobal
              ? i18next.t('taxtype-refresh-confirm-message-global')
              : i18next.t('taxtype-refresh-confirm-message-space')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelRefreshDialog}>
            {i18next.t('cancel')}
          </Button>
          <Button onClick={handleConfirmRefresh} variant="contained" autoFocus>
            {i18next.t('continue')}
          </Button>
        </DialogActions>
      </Dialog>

      <Grid container spacing={3}>
        <UnsavedChangesPrompt isDirty={isDirty} />
        {/* Refresh progress + cancel – placed at the TOP so it's always visible */}
        {shouldShowRefreshBox && (
          <Grid item xs={12}>
            <Box sx={{ mb: 0, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" fontWeight="medium">
                  {i18next.t('taxtype-refresh-progress')}
                </Typography>
                <Button
                  size="small"
                  variant="contained"
                  color="secondary"
                  startIcon={<CancelIcon />}
                  onClick={handleCancelRefresh}
                >
                  {i18next.t('taxtype-refresh-cancel')}
                </Button>
              </Box>
              <LinearProgress
                variant={isRefreshStarting && !isRefreshing ? 'indeterminate' : 'determinate'}
                value={isRefreshStarting && !isRefreshing ? 0 : refreshProgress}
                sx={{ height: 8, borderRadius: 4, mb: 1 }}
              />
              <Typography variant="caption" color="textSecondary">
                {isRefreshStarting && !isRefreshing
                  ? i18next.t('taxtype-refresh-starting')
                  : isRefreshing
                    ? (isGlobal && spacesTotal > 0
                      ? `${spacesDone} / ${spacesTotal} ${i18next.t('taxtype-refresh-spaces')} • ${hotelsDone} / ${hotelsTotal} ${i18next.t('taxtype-refresh-hotels')}`
                      : `${hotelsDone} / ${hotelsTotal} ${i18next.t('taxtype-refresh-hotels')}`)
                    : i18next.t('taxtype-refresh-idle')}{' '}
                {isRefreshing && `(${Math.round(refreshProgress)}%)`}
                {!isRefreshStarting && isRefreshing && isGlobal && currentSpaceId != null && (
                  <span>
                    {' '}
                    • {i18next.t('taxtype-refresh-current-space')}:{' '}
                    {currentSpaceName ? `${currentSpaceName} (${currentSpaceId})` : currentSpaceId}
                  </span>
                )}
              </Typography>
              {refreshError && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {refreshError}
                </Alert>
              )}
            </Box>
          </Grid>
        )}
        <Grid item xs={12} sm={6}>
          <FormInputText name="name" control={control} label={i18next.t('taxtype-name')} required disabled={!canEdit} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <SpaceSelectionInput
            checkAdmin
            name="spaceId"
            control={control}
            disabled={!canEdit || props.data.id > 0 || user.isSingleAdminSpace}
            allowEmptyForRoot
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormInputNumber name="sequence" label={i18next.t('taxtype-sequence')} control={control} required disabled={!canEdit} />
        </Grid>
        <Grid item xs={12}>
          <CustomTabs
            headers={[i18next.t('taxtype-rates-tab')]}
            tabs={[
              <>
                <SimpleTable
                  rows={ratesFields.map((field, index) => [
                    <FormInputPercentage
                      key={`${field.id}.rate`}
                      name={`rates.${index}.rate`}
                      control={control}
                      label={i18next.t('taxtype-rate-rate')}
                      required
                      disabled={!canEdit}
                    />,
                    <FormInputText
                      key={`${field.id}.name`}
                      name={`rates.${index}.name`}
                      control={control}
                      label={i18next.t('taxtype-rate-name')}
                      required
                      disabled={!canEdit}
                    />,
                    <FormInputCountryDropdown
                      key={`${field.id}.country`}
                      name={`rates.${index}.country`}
                      control={control}
                      label={i18next.t('taxtype-rate-country')}
                      disabled={!canEdit}
                    />,
                    <FormInputDate
                      key={`${field.id}.validFrom`}
                      name={`rates.${index}.validFrom`}
                      control={control}
                      label={i18next.t('taxtype-rate-validfrom')}
                      textFieldProps={{ fullWidth: false }}
                      disabled={!canEdit}
                    />,
                    <FormInputDate
                      key={`${field.id}.validTo`}
                      name={`rates.${index}.validTo`}
                      control={control}
                      label={i18next.t('taxtype-rate-validto')}
                      textFieldProps={{ fullWidth: false }}
                      disabled={!canEdit}
                    />,
                    <FormInputCheckbox
                      key={`${field.id}.isReverseCharge`}
                      name={`rates.${index}.isReverseCharge`}
                      control={control}
                      label={i18next.t('taxtype-rate-isreversecharge')}
                      disabled={!canEdit}
                    />,
                    canEdit ? (
                      <IconButton onClick={() => ratesRemove(index)}>
                        <DeleteIcon />
                      </IconButton>
                    ) : null,
                  ])}
                />
                {canEdit && (
                  <Button
                    sx={{ marginRight: 2 }}
                    startIcon={<AddIcon />}
                    onClick={() =>
                      ratesAppend({
                        rate: 0,
                        name: '',
                        country: null,
                        isReverseCharge: false,
                      })
                    }
                  >
                    {i18next.t('taxtype-rate-add')}
                  </Button>
                )}
              </>,
            ]}
          />
        </Grid>

        <Grid item xs={12}>
          {canEdit && (
            <Button
              sx={{ marginRight: 2 }}
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={(props.data.id > 0 && !isDirty) || isSubmitting || isValidating || isRefreshing || isRefreshStarting}
              onClick={async () => {
                const valid = await trigger();
                if (valid) {
                  handleSubmit(onSubmit)();
                }
              }}
            >
              {i18next.t('taxtype-save')}
            </Button>
          )}
          {canEdit && props.data.id > 0 && (
            <ConfirmationButton
              sx={{ marginRight: 2 }}
              variant="contained"
              color="secondary"
              startIcon={<DeleteIcon />}
              confirmationQuestion={i18next.t('taxtype-confirm-delete')}
              confirmationTitle={i18next.t('taxtype-confirm-delete-title')}
              icon={false}
              onConfirm={async () => {
                try {
                  const res = await deleteMutateFunction({
                    variables: {
                      id: props.data.id,
                    },
                    update: cache => EVICT_TAXTYPES_QUERIES(cache),
                    awaitRefetchQueries: true,
                    refetchQueries: REFETCH_TAXTYPES_QUERIES(),
                  });
                  navigate('/administration/taxtypes');
                  dispatchMessage(dispatch, i18next.t('taxtype-deleted'));
                } catch (err) {
                  dispatchException(dispatch, err);
                }
              }}
            >
              {i18next.t('taxtype-delete')}
            </ConfirmationButton>
          )}
        </Grid>
      </Grid>
    </>
  );
}

export default function TaxType(props: TaxTypeProps) {
  const taxTypeQuery = useQuery(TAXTYPE_VIEW_QUERY, {
    variables: { id: props.id },
  });

  const loading = taxTypeQuery.loading;
  const error = taxTypeQuery.error;

  if (loading) return <CircularProgress />;
  else if (!loading && error) return <RedirectError err={error} />;
  else return <TaxTypeForm data={taxTypeQuery.data!.viewTaxType as TaxTypeListOutput} />;
}

export function TaxTypeCreate(props: TaxTypeCreateProps) {
  const filter = filterSelector();
  const user = userSelector()!;

  return (
    <TaxTypeForm
      data={{
        id: -1,
        space: { id: initialSpaceId(user, filter) } as any,
        sequence: 0,
        name: '',
        rates: [{ id: -1, name: '', rate: 0, isReverseCharge: false }],
      }}
    />
  );
}
